import {
  Grid2x2,
  Hamburger,
  CupSoda,
  IceCreamCone,
  BadgePercent,
  UtensilsCrossed,
  Pizza,
  Beef,
  Drumstick,
  Flame,
  Beer,
  Wine,
  Coffee,
  Salad,
  Soup,
  Croissant,
  Shrimp,
  ChefHat,
  Star,
  Cake,
  Gift,
} from 'lucide-react';

import {
  PapasFritasIcon,
  SushiIcon,
  HandRollIcon,
  TacoIcon,
  WaffleIcon,
  CompletoIcon,
  PolloAsadoIcon,
  PastaIcon,
} from '@/lib/menuCategoryCustomIcons';
import { normalizeMenuBlob, resolveMenuFamilyFromTextLoose } from '@/lib/menuSemanticTags';

/** Icono por familia semántica (misma lógica que el asistente del menú). */
const FAMILY_ICONS = {
  hamburguesa: Hamburger,
  sushi: SushiIcon,
  hand_roll: HandRollIcon,
  poke: SushiIcon,
  pizza: Pizza,
  parrilla: Beef,
  completo: CompletoIcon,
  sandwich: CompletoIcon,
  mex: TacoIcon,
  pollo_frito: Drumstick,
  pollo_asado: PolloAsadoIcon,
  papas_topping: PapasFritasIcon,
  cerveza: Beer,
  licor: Wine,
  colaciones: Gift,
  empanada: Croissant,
  pasta: PastaIcon,
  ramen: Soup,
  wok: Flame,
  mechada: Beef,
  platos_preparados: ChefHat,
};

/** Promos, combos y packs (ej. "promo para 3", "2x1", "combo familiar"). */
const PROMO_RULES = [
  {
    keys: [
      'promo',
      'combo',
      'oferta',
      'descuent',
      'pack',
      'deal',
      '2x1',
      '3x2',
      'para 2',
      'para 3',
      'para 4',
      'para dos',
      'para tres',
      'x2',
      'x3',
      'triplo',
      'triple',
      'duo',
      'dúo',
    ],
    Icon: BadgePercent,
  },
];

/** Categorías de navegación (no son familias de producto). */
const META_RULES = [
  { keys: ['todos', 'todas', 'all', 'ver todo', 'ver todos'], Icon: Grid2x2 },
  {
    keys: ['influencer', 'destacad', 'signature', 'house special', 'chef choice', 'premium', 'edicion'],
    Icon: Star,
  },
];

/** Señales extra en el nombre de la categoría. */
const EXTRA_RULES = [
  { keys: ['hamburg', 'burger', 'smash'], Icon: Hamburger },
  { keys: ['hand roll', 'handroll', 'handrolls', 'temaki'], Icon: HandRollIcon },
  { keys: ['sushi', 'maki', 'sashimi', 'nigiri', 'omakase', 'uramaki', 'hosomaki'], Icon: SushiIcon },
  { keys: ['pizza', 'piza'], Icon: Pizza },
  { keys: ['waffle', 'wafle', 'gofre'], Icon: WaffleIcon },
  { keys: ['taco', 'taquito', 'burrito', 'quesadilla', 'nacho'], Icon: TacoIcon },
  { keys: ['completo', 'hot dog', 'hotdog', 'vienesa'], Icon: CompletoIcon },
  {
    keys: ['pollo asado', 'pollo a la brasa', 'pollo brasa', 'pollo al spiedo', 'pollo spiedo', 'pollo al horno'],
    Icon: PolloAsadoIcon,
  },
  { keys: ['pasta', 'pastas', 'spaghetti', 'fetuccini', 'fettuccine', 'ravioles', 'ravioli', 'lasagna', 'ñoqui', 'gnocchi'], Icon: PastaIcon },
  { keys: ['suprema', 'supremas', 'pollo frito', 'alita', 'wings', 'broaster'], Icon: Drumstick },
  { keys: ['marisco', 'camar', 'langost', 'pulpo', 'ceviche'], Icon: Shrimp },
  { keys: ['pescad', 'salmon', 'atun', 'reineta'], Icon: Shrimp },
  { keys: ['bebida', 'drink', 'jugo', 'gaseosa', 'refresco', 'agua mineral', 'smoothie'], Icon: CupSoda },
  { keys: ['cerveza', 'chela', 'schop', 'ipa', 'lager'], Icon: Beer },
  { keys: ['vino', 'cocktail', 'coctel', 'tragos', 'pisco', 'whisky', 'bar '], Icon: Wine },
  { keys: ['cafe', 'café', 'espresso', 'capuccino', 'cappuccino', 'latte'], Icon: Coffee },
  { keys: ['postre', 'dulce', 'helado', 'brownie', 'alfajor', 'donut'], Icon: IceCreamCone },
  { keys: ['torta', 'pastel', 'cake'], Icon: Cake },
  { keys: ['ensalada', 'vegetal', 'vegan', 'veggie'], Icon: Salad },
  { keys: ['sopa', 'caldo', 'crema de', 'ramen'], Icon: Soup },
  { keys: ['desayuno', 'brunch', 'panader', 'pasteler'], Icon: Croissant },
  {
    keys: ['snack', 'papa', 'papas', 'frita', 'fritas', 'acompañ', 'acompa', 'side', 'sides'],
    Icon: PapasFritasIcon,
  },
];

function norm(s) {
  return normalizeMenuBlob(s);
}

function matchRules(rules, n) {
  for (const { keys, Icon } of rules) {
    for (const k of keys) {
      const key = norm(k);
      if (!key) continue;
      if (key.includes(' ')) {
        if (n.includes(key)) return Icon;
        continue;
      }
      const esc = key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      if (new RegExp(`\\b${esc}\\b`).test(n) || n.includes(key)) return Icon;
    }
  }
  return null;
}

function businessBlob(business) {
  if (!business) return '';
  return [business.name, business.slogan, business.description].filter(Boolean).join(' ');
}

/**
 * @param {string} categoryName
 * @param {{ business?: { name?: string, slogan?: string, description?: string } }} [options]
 * @returns {import('react').ComponentType<{ className?: string, style?: object }>}
 */
export function getMenuCategoryIcon(categoryName, options = {}) {
  const n = norm(categoryName);

  const promo = matchRules(PROMO_RULES, n);
  if (promo) return promo;

  const meta = matchRules(META_RULES, n);
  if (meta) return meta;

  const extra = matchRules(EXTRA_RULES, n);
  if (extra) return extra;

  const family = resolveMenuFamilyFromTextLoose(categoryName);
  if (family && FAMILY_ICONS[family]) return FAMILY_ICONS[family];

  const bizFamily = resolveMenuFamilyFromTextLoose(businessBlob(options.business));
  if (bizFamily && FAMILY_ICONS[bizFamily]) return FAMILY_ICONS[bizFamily];

  return UtensilsCrossed;
}
